import {
  SimpleFilterConfiguration,
  SimpleFilterDeviceDataType,
  SimpleFilterType,
} from '../interfaces'
import { Injectable, inject } from '@angular/core'
import { ApolloQueryResult } from '@apollo/client'
import { SimpleFilterStateModel } from '../simple-filter.state'
import { FetchBuildingFromDeviceIdGQL } from '../graphql/fetch-building-from-device-id.types'
import { firstValueFrom } from 'rxjs'
import { L10nService } from '@wm-front/admin/l10n'
import {
  DeviceSpecFilterOption,
  DeviceTypeEnum,
  RootWhereInput,
} from '@wm-front/admin/graphql'
import { i18nDeviceType } from '@wm-front/shared/utils'
import { DropdownOption } from '@wm-front/core'
import {
  FetchDevicesOptionsGQL,
  FetchDevicesOptionsQuery,
} from '../graphql/fetch-devices-options.types'
import { dataToDeviceOption } from '../../helpers/device-options.utils'
import {
  FetchDeviceSpecsGQL,
  FetchDeviceSpecsQuery,
} from '../graphql/fetch-device-specs-options.types'
import { DeviceType, DeviceTypePipe } from '@wm-front/shared/pipes'

/**
 * Will hold the default filter configuration
 */
@Injectable({ providedIn: 'root' })
export class DeviceFiltersCatalogService {
  protected _fetchDeviceOptionsGql = inject(FetchDevicesOptionsGQL)

  protected _fetchDeviceSpecsOptionsGql = inject(FetchDeviceSpecsGQL)

  protected _fetchBuildingFromDeviceId = inject(FetchBuildingFromDeviceIdGQL)

  protected _l10nService = inject(L10nService)

  public getConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<any>
  ): SimpleFilterConfiguration<any> {
    if (overrideConfiguration?.dataType !== undefined) {
      switch (overrideConfiguration.dataType) {
        case SimpleFilterDeviceDataType.DEVICE_TYPES:
          return this.getDeviceTypesFilterConfiguration(overrideConfiguration)
        case SimpleFilterDeviceDataType.DEVICES:
          return this.getDevicesFilterConfiguration(overrideConfiguration)
        case SimpleFilterDeviceDataType.DEVICE_STATES:
          return this.getDeviceStatesFilterConfiguration(overrideConfiguration)
        case SimpleFilterDeviceDataType.ONBOARDING_ASSIGNED_OPS_REFERENT:
          return this.getAssignedOpsReferentFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterDeviceDataType.ONBOARDING_ASSIGNED_MECHANIC:
          return this.getAssignedMechanicFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterDeviceDataType.ONBOARDING_FIRST_MAINTENANCE_COMPLETED:
          return this.getFirstMaintenanceCompletedFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterDeviceDataType.ONBOARDING_STICKER_INSTALLED:
          return this.getStickerInstalledFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterDeviceDataType.ONBOARDING_AUTODIALER_REPROGRAMMED:
          return this.getAutodialerReprogrammedFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterDeviceDataType.ONBOARDING_LOG_CARD_INSTALLED:
          return this.getLogCardInstalledFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterDeviceDataType.ONBOARDING_SECURITY_AUDIT_COMPLETED:
          return this.getSecurityAuditCompletedFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterDeviceDataType.ONBOARDING_HANDOVER_COMPLETED:
          return this.getHandoverCompletedFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterDeviceDataType.ONBOARDING_CONTRACT_START_DATE:
          return this.getContractStartDateFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterDeviceDataType.ISSUES:
          return this.getDeviceWithIssueFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterDeviceDataType.STOPPED:
          return this.getDeviceStoppedFilterConfiguration(overrideConfiguration)
        case SimpleFilterDeviceDataType.SPECS:
          return this.getSpecsFilterConfiguration(overrideConfiguration)
      }
    }
    return overrideConfiguration
  }

  private getDeviceTypesFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: overrideConfiguration.key || 'deviceTypes',
      display: overrideConfiguration.display || {
        label: $localize`:Label | Device types default label@@filtersCatalogDeviceTypesLabel:Type d'app.`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      data: Object.keys(DeviceTypeEnum).map((type) => {
        return {
          label: i18nDeviceType(DeviceTypeEnum[type]),
          value: DeviceTypeEnum[type],
        }
      }),
    }
  }

  private getDeviceStatesFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'deviceStates',
      display: overrideConfiguration.display || {
        label: $localize`:Label | Device state default label@@filtersCatalogDeviceStateLabel:Unit status`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      data: [
        {
          label: $localize`:Label | Label for working option on device state filter@@filtersCatalogWorkingDeviceStateFilterOption:Working`,
          value: 'working',
        },
        {
          label: $localize`:Label | Label for stopped option on device state filter@@filtersCatalogStoppedDeviceStateFilterOption:Stopped`,
          value: 'stopped',
        },
        {
          label: $localize`:Label | Label for paused option on device state filter@@filtersCatalogPausedDeviceStateFilterOption:Paused`,
          value: 'paused',
        },
      ],
    }
  }

  private getDeviceStoppedFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'stopped',
      display: overrideConfiguration.display || {
        label: $localize`:Label | Device state default label@@filtersCatalogStoppedLabel:Stopped`,
      },
      type: SimpleFilterType.TOGGLE,
      withoutSearch: true,
      radioButtonMode: true,
      data: [
        {
          label: $localize`:@@adminDevicesConditionsStoppedFilterBreadownButton:Mis à l'arrêt`,
          value: true,
          checked: true,
        },
        {
          label: $localize`:@@adminDevicesConditionsStoppedFilterStoppedLabel:Panne`,
          value: false,
        },
      ],
    }
  }

  private getDeviceWithIssueFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'statuses',
      urlName: 'withIssue',
      display: overrideConfiguration.display || {
        label: $localize`:Label | Device state default label@@filtersCatalogWithIssueLabel:With issue`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      radioButtonMode: false,
      data: [
        {
          label: $localize`:@@adminDevicesConditionsWorkingFilterWithIssueButton:Avec anomalie(s)`,
          value: 'HAS_ISSUES',
        },
        {
          label: $localize`:@@adminDevicesConditionsWorkingFilterWithNoIssueButton:Sans anomalie`,
          value: 'WORKING',
        },
      ],
    }
  }

  private getDevicesFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<FetchDevicesOptionsQuery>
  ): SimpleFilterConfiguration<FetchDevicesOptionsQuery> {
    return {
      key: 'deviceIds',
      type: SimpleFilterType.DROPDOWN,
      display: {
        label: $localize`:Device filter label | label for the device filter@@deviceFiltersCatalogServiceDeviceLabel:Devices`,
        placeholder: $localize`:Device filter label | placeholder for the device filter@@deviceFiltersCatalogServiceDevicePlaceholder:Trouver un device`,
      },
      dataGetter: this._fetchDeviceOptionsGql,
      dropdownOptionConverter: (
        data: ApolloQueryResult<FetchDevicesOptionsQuery>
      ) => {
        return data.data.searchResult.reduce((previous, current) => {
          const buildingsIds: (string | number | boolean)[] = previous.length
            ? previous.map((item: DropdownOption) => item.value)
            : []
          const device: DropdownOption = dataToDeviceOption(current)
          if (
            current.building.id &&
            buildingsIds.includes(current.building.id)
          ) {
            const index = buildingsIds.indexOf(current.building.id)
            previous[index].children?.push(device)
          } else {
            previous = [
              ...previous,
              {
                label: `${current.building.name} - ${current.building.address}, ${current.building.zipcode} ${current.building.city}`,
                value: current.building.id?.toString() || '',
                children: [device],
              },
            ]
          }
          return previous
        }, [] as DropdownOption[])
      },
      paginationConverter: (
        data: ApolloQueryResult<FetchDevicesOptionsQuery>,
        state?: SimpleFilterStateModel
      ) => {
        const startCursor =
          (state as SimpleFilterStateModel)['deviceIds'].data
            ?.reduce(
              (previous, current) =>
                previous + (current?.children?.length || 0),
              data.data.searchResult.length
            )
            .toString() || data.data.searchResult?.length.toString()
        return {
          startCursor,
          endCursor: '',
          hasNextPage: true,
          hasPreviousPage: false,
        }
      },
      filters: {
        buildingWhereInput: {
          countryCode: { equals: this._l10nService.territoryLocale },
        },
      },
      bodyConverter: (filters, state, externalState, pagination, reset) => {
        filters = { ...filters }
        const buildingWhereInput = { ...filters.buildingWhereInput }
        if (externalState?.['buildingIds']?.length) {
          buildingWhereInput.id = {
            in: externalState['buildingIds'],
          }
        }
        if (externalState?.['accountIds']?.length) {
          buildingWhereInput.accountIds = {
            in: externalState['accountIds'],
          }
        }
        filters.buildingWhereInput = buildingWhereInput
        return {
          ...filters,
          search: filters.search,
          limit: 10,
          offset: !reset ? Number(pagination?.startCursor) || 0 : 0,
        }
      },
      fetchSelectedDataBodyConverter: async (value, _, externalState) => {
        if (value?.length) {
          const accountIds = externalState?.['accountIds']?.length
            ? externalState?.['accountIds']
            : []
          const whereInput: RootWhereInput = { device: { id: { in: value } } }
          if (accountIds?.length) {
            whereInput.building = { accountIds: { in: accountIds } }
          }
          const buildings = await firstValueFrom(
            this._fetchBuildingFromDeviceId.fetch({ whereInput })
          )
          const result = buildings.data.result
          if (result?.length) {
            return {
              buildingWhereInput: {
                id: { in: result.map((r) => r.building.id) },
                ...(externalState?.['accountIds']?.length
                  ? { accountIds: { in: externalState['accountIds'] } }
                  : {}),
              },
            }
          }
        }
        return undefined
      },
      disabled: overrideConfiguration.disabled,
    }
  }

  private getAssignedOpsReferentFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'assignedOpsReferent',
      display: overrideConfiguration.display || {
        label: $localize`:Label | Is assigned ops referent filter label@@filtersCatalogAssignedOpsReferentLabel:Ops assign.`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      radioButtonMode: true,
      data: [
        {
          label: $localize`:Label | Yes option@@filtersCatalogAssignedOpsReferentYes:Yes`,
          value: true,
        },
        {
          label: $localize`:Label | No option@@filtersCatalogAssignedOpsReferentNo:No`,
          value: false,
        },
      ],
    }
  }

  private getAssignedMechanicFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'assignedMechanic',
      display: overrideConfiguration.display || {
        label: $localize`:Label | Is assigned mechanic filter label@@filtersCatalogAssignedMechanicLabel:Tech assign.`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      radioButtonMode: true,
      data: [
        {
          label: $localize`:Label | Yes option@@filtersCatalogAssignedMechanicYes:Yes`,
          value: true,
        },
        {
          label: $localize`:Label | No option@@filtersCatalogAssignedMechanicNo:No`,
          value: false,
        },
      ],
    }
  }

  private getFirstMaintenanceCompletedFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'firstMaintenanceCompleted',
      display: overrideConfiguration.display || {
        label: $localize`:Label | First maintenance completed filter label@@filtersCatalogFirstMaintenanceCompletedLabel:1ère visite`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      radioButtonMode: true,
      data: [
        {
          label: $localize`:Label | Yes option@@filtersCatalogFirstMaintenanceCompletedYes:Yes`,
          value: true,
        },
        {
          label: $localize`:Label | No option@@filtersCatalogFirstMaintenanceCompletedNo:No`,
          value: false,
        },
      ],
    }
  }

  private getStickerInstalledFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'stickerInstalled',
      display: overrideConfiguration.display || {
        label: $localize`:Label | Sticker installed filter label@@filtersCatalogStickerInstalledLabel:Stickers`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      radioButtonMode: true,
      data: [
        {
          label: $localize`:Label | Yes option@@filtersCatalogStickerInstalledYes:Yes`,
          value: true,
        },
        {
          label: $localize`:Label | No option@@filtersCatalogStickerInstalledNo:No`,
          value: false,
        },
      ],
    }
  }

  private getAutodialerReprogrammedFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'autodialerReprogrammed',
      display: overrideConfiguration.display || {
        label: $localize`:Label | Autodialer reprogrammed filter label@@filtersCatalogAutodialerReprogrammedLabel:Téléalarme`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      radioButtonMode: true,
      data: [
        {
          label: $localize`:Label | Yes option@@filtersCatalogAutodialerReprogrammedYes:Yes`,
          value: true,
        },
        {
          label: $localize`:Label | No option@@filtersCatalogAutodialerReprogrammedNo:No`,
          value: false,
        },
      ],
    }
  }

  private getLogCardInstalledFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'logCardInstalled',
      display: overrideConfiguration.display || {
        label: $localize`:Label | Log card installed filter label@@filtersCatalogLogCardInstalledLabel:Log Card`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      radioButtonMode: true,
      data: [
        {
          label: $localize`:Label | Yes option@@filtersCatalogLogCardInstalledYes:Yes`,
          value: true,
        },
        {
          label: $localize`:Label | No option@@filtersCatalogLogCardInstalledNo:No`,
          value: false,
        },
      ],
    }
  }

  private getSecurityAuditCompletedFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'securityAuditCompleted',
      display: overrideConfiguration.display || {
        label: $localize`:Label | Security audit completed filter label@@filtersCatalogSecurityAuditCompletedLabel:EDS`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      radioButtonMode: true,
      data: [
        {
          label: $localize`:Label | Yes option@@filtersCatalogSecurityAuditCompletedYes:Yes`,
          value: true,
        },
        {
          label: $localize`:Label | No option@@filtersCatalogSecurityAuditCompletedNo:No`,
          value: false,
        },
      ],
    }
  }

  private getHandoverCompletedFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'handoverCompleted',
      display: overrideConfiguration.display || {
        label: $localize`:Label | Handover completed filter label@@filtersCatalogHandoverCompletedLabel:EDL`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      radioButtonMode: true,
      data: [
        {
          label: $localize`:Label | Yes option@@filtersCatalogHandoverCompletedYes:Yes`,
          value: true,
        },
        {
          label: $localize`:Label | No option@@filtersCatalogHandoverCompletedNo:No`,
          value: false,
        },
      ],
    }
  }

  private getContractStartDateFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'period',
      display: overrideConfiguration.display || {
        label: $localize`:Label | Device state default label@@filtersCatalogContractStartDateLabel:Contract start date`,
      },
      type: SimpleFilterType.PERIOD,
    }
  }

  private getSpecsFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<FetchDeviceSpecsQuery>
  ): SimpleFilterConfiguration<FetchDeviceSpecsQuery> {
    return {
      key: 'specsFilters',
      type: SimpleFilterType.DROPDOWN,
      display: {
        label: $localize`:@@deviceFiltersCatalogServiceDeviceSpecsLabel:Specs`,
        placeholder: $localize`:@@deviceFiltersCatalogServiceDeviceSpecsPlaceholder:Trouver une spec.`,
      },
      dataGetter: this._fetchDeviceSpecsOptionsGql,
      withoutBackendFiltering: true,
      dropdownOptionConverter: (
        data: ApolloQueryResult<FetchDeviceSpecsQuery>
      ) => {
        const deviceTypePipe = new DeviceTypePipe()
        return data.data.deviceSpecsFilterOptions.reduce((acc, next) => {
          const type = deviceTypePipe.transform(next.deviceType as any)
          const options = next.specs
            .filter(
              (spec) =>
                ![
                  'liftInstallationYear',
                  'subtype',
                  'motorYear',
                  'controllerYear',
                ].includes(spec.key.split('.').pop() || '')
            )
            .map((option) => {
              return {
                label: `${type} - ${option.title}`,
                value: option.key,
                children: [
                  ...option.options.map((opt) => ({
                    label: opt.label,
                    value: `${option.key}·${opt.value}`,
                    entity: option,
                  })),
                  {
                    label: $localize`:@@deviceFiltersCatalogServiceDeviceSpecsEmptyLabel:Empty`,
                    value: `${option.key}·${null}`,
                    entity: option,
                    style: 'italic',
                  },
                ],
              }
            })
          return [...acc, ...options]
        }, [] as DropdownOption[])
      },
      disabled: overrideConfiguration.disabled,
    }
  }
}
